import {
  generateMyNotes,
  generateNearbyNotes,
  SAMPLE_COMMENTS,
} from "../data/mockNotes";
import { CURRENT_USER, MOCK_USERS } from "../data/mockUsers";
import {
  generateNotifications,
  NOTIFICATION_TYPES,
} from "../data/mockNotifications";
import { devLog, logError } from "../utils/logger";

// Simulate network delay
const delay = (ms = 300) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Helper to get user by ID
 * Simulates backend user lookup
 * In real backend, this would query the database
 */
function getUserById(userId) {
  // Check if it's the current user
  if (userId === CURRENT_USER.id) {
    return CURRENT_USER;
  }

  // Check mock users
  const user = MOCK_USERS.find((u) => u.id === userId);
  if (!user) {
    throw new Error(`User not found: ${userId}`);
  }

  return user;
}

// Calculate distance between two coordinates (Haversine formula)
function getDistance(lat1, lon1, lat2, lon2) {
  const R = 3958.7613; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

class MockAPI {
  constructor() {
    devLog("MockAPI: Initializing...");
    try {
      // Separate stores for different contexts
      this.myNotes = generateMyNotes(45);
      devLog("MockAPI: Generated my notes:", this.myNotes.length);
      this.nearbyNotesCache = new Map(); // Cache by location key
      this.nextId = 1000;

      // All notes store - includes myNotes and default nearby notes
      // This ensures notes can be found even in fresh tabs (for shared links)
      const defaultNearbyNotes = generateNearbyNotes(37.7749, -122.4194, 50);
      this.allNotesStore = [...this.myNotes, ...defaultNearbyNotes];
      devLog("MockAPI: All notes store:", this.allNotesStore.length);

      // Notifications store (per user)
      this.notificationsStore = new Map(); // userId -> notifications array

      // User settings store (per user)
      this.userSettingsStore = new Map(); // userId -> settings object
      // Initialize with CURRENT_USER's settings
      if (CURRENT_USER.settings) {
        this.userSettingsStore.set(CURRENT_USER.id, CURRENT_USER.settings);
      }

      // Reports store
      this.reportsStore = []; // Array of report objects

      // Feedback store (improvements, bugs, support requests)
      this.feedbackStore = []; // Array of feedback objects

      devLog("MockAPI: Initialization complete");
    } catch (err) {
      logError("MockAPI: Initialization error:", err);
      throw err;
    }
  }

  // GET /api/notes/my/stats - Get note counts by visibility
  async getMyNotesStats() {
    await delay(800);

    const stats = {
      all: this.myNotes.length,
      public: 0,
      private: 0,
    };

    for (const note of this.myNotes) {
      if (note.visibility === "public") {
        stats.public += 1;
      } else if (note.visibility === "private") {
        stats.private += 1;
      }
    }

    return { data: stats };
  }

  // GET /api/notes/my - For MyNotesPage
  async getMyNotes({ filter = "all", limit = 50, offset = 0 } = {}) {
    await delay(1000);

    /**
     * call getMyNotesStats (/api/notes/my/stats API) to get total number of notes
     */
    let filtered = this.myNotes;

    if (filter !== "all") {
      filtered = this.myNotes.filter((n) => n.visibility === filter);
    }

    // Sort by creation date (newest first)
    filtered = [...filtered].sort((a, b) => b.createdAt - a.createdAt);

    const paginatedNotes = filtered.slice(offset, offset + limit);

    return {
      data: paginatedNotes,
      total: filtered.length,
      hasMore: offset + limit < filtered.length,
      filter,
    };
  }

  // GET /api/notes/nearby - For Home.js
  async getNearbyNotes({ latitude, longitude, radius = 1, unit = 'mi', limit = 50 } = {}) {
    await delay(400);

    const locationKey = `${latitude.toFixed(3)},${longitude.toFixed(3)}`;

    // Generate or retrieve cached nearby notes for this location
    if (!this.nearbyNotesCache.has(locationKey)) {
      const generated = generateNearbyNotes(latitude, longitude, 25);
      devLog("Generated Near Notes:", generated);
      this.nearbyNotesCache.set(locationKey, generated);
    }

    const nearbyNotes = this.nearbyNotesCache.get(locationKey);

    // Calculate distances and filter by radius
    const withDistance = nearbyNotes.map((note) => ({
      ...note,
      distance: getDistance(latitude, longitude, note.latitude, note.longitude),
    }));

    const filtered = withDistance
      .filter((note) => note.distance <= radius)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, limit);

    return {
      data: filtered,
      total: filtered.length,
      center: { latitude, longitude },
    };
  }

  // GET /api/notes/nearby/new — Legacy: notes posted near user since last logout
  async getNewNearbyNotes({ radius = 5, unit = "mi", limit = 50 } = {}) {
    await delay(400);

    const since = Date.now() - 24 * 60 * 60 * 1000; // Simulate 24h since last logout

    // Collect from nearbyNotesCache (same notes shown on the map)
    const allNearby = [];
    for (const notes of this.nearbyNotesCache.values()) {
      allNearby.push(...notes);
    }

    const newNotes = allNearby
      .filter((n) => n.createdAt > since && !n.isViewedByCurrentUser && !n.isOwner)
      .slice(0, limit);

    return {
      data: newNotes,
      total: newNotes.length,
      since: new Date(since).toISOString(),
      center: null,
    };
  }

  // GET /api/notes/:id
  async getNote(id) {
    await delay(200);

    // Search in myNotes first
    let note = this.myNotes.find((n) => n.id === id);

    if (!note) {
      // Search in all cached nearby notes
      for (const notes of this.nearbyNotesCache.values()) {
        note = notes.find((n) => n.id === id);
        if (note) break;
      }
    }

    if (!note) {
      // Fallback: Search in allNotesStore (for shared links in fresh tabs)
      note = this.allNotesStore.find((n) => n.id === id);
    }

    if (!note) throw new Error("Note not found");

    // Simulate backend auto-recording the view
    note.isViewedByCurrentUser = true;

    return { data: note };
  }

  // POST /api/notes
  // Real backend: Gets userId from auth token in request headers
  async createNote(noteData, userId = CURRENT_USER.id) {
    await delay(400);

    // Get user info (simulates backend user lookup)
    const user = getUserById(userId);

    const createdAt = Date.now();
    const newNote = {
      id: `note-${this.nextId++}`,
      owner: {
        id: user.id,
        name: user.name,
        avatar: user.avatar,
      },
      ...noteData,
      locationPrecision: noteData.locationPrecision || "exact",
      showAddress: noteData.showAddress !== undefined ? noteData.showAddress : true,
      stats: { likes: 0, comments: 0 },
      comments: [],
      createdAt,
      isOwner: true,
      isLikedByCurrentUser: false,
    };

    // Add to my notes
    this.myNotes.unshift(newNote);

    // If public, also add to nearby notes in relevant caches
    // and notify nearby users
    if (newNote.visibility === "public") {
      for (const [key, notes] of this.nearbyNotesCache.entries()) {
        notes.unshift(newNote);
      }

      // Generate a "nearby_note" notification for each other mock user
      const otherUsers = MOCK_USERS.filter((u) => u.id !== userId);
      for (const recipient of otherUsers) {
        const notifications = this._getUserNotifications(recipient.id);
        notifications.unshift({
          id: `notif-nearby-${newNote.id}-${recipient.id}`,
          type: NOTIFICATION_TYPES.NEARBY,
          noteId: newNote.id,
          noteTitle: newNote.title,
          placeName: newNote.address?.split(",")[1]?.trim() || null,
          coords:
            newNote.latitude != null
              ? { lat: newNote.latitude, lng: newNote.longitude }
              : null,
          message: `${user.name} left a trace near you`,
          createdAt: Date.now(),
          read: false,
          userId: recipient.id,
          actorName: user.name,
          avatarUrl: user.avatar,
        });
      }
    }

    return { data: newNote };
  }

  // PATCH /api/notes/:id
  // Real backend: Gets userId from auth token in request headers
  async updateNote(id, updates, userId = CURRENT_USER.id) {
    await delay(300);

    if (!userId) {
      throw new Error("User not authenticated");
    }

    // Add editedAt timestamp
    const updatesWithTimestamp = {
      ...updates,
      editedAt: Date.now(),
    };

    // First, try to find and update in myNotes
    const myIndex = this.myNotes.findIndex((n) => n.id === id);
    if (myIndex !== -1) {
      // Verify ownership
      if (this.myNotes[myIndex].owner.id !== userId) {
        throw new Error("Not authorized to update this note");
      }

      this.myNotes[myIndex] = {
        ...this.myNotes[myIndex],
        ...updatesWithTimestamp,
      };

      // Update in nearby caches if exists
      for (const notes of this.nearbyNotesCache.values()) {
        const nearbyIndex = notes.findIndex((n) => n.id === id);
        if (nearbyIndex !== -1) {
          notes[nearbyIndex] = {
            ...notes[nearbyIndex],
            ...updatesWithTimestamp,
          };
        }
      }

      return { data: this.myNotes[myIndex] };
    }

    // If not in myNotes, check nearbyNotesCache (for notes viewed from Home page)
    for (const notes of this.nearbyNotesCache.values()) {
      const nearbyIndex = notes.findIndex((n) => n.id === id);
      if (nearbyIndex !== -1) {
        const note = notes[nearbyIndex];

        // Verify ownership
        if (note.owner.id !== userId) {
          throw new Error("Not authorized to update this note");
        }

        // Update the note
        notes[nearbyIndex] = { ...note, ...updatesWithTimestamp };

        // Also add/update in myNotes if it's the user's note
        const myNoteIndex = this.myNotes.findIndex((n) => n.id === id);
        if (myNoteIndex !== -1) {
          this.myNotes[myNoteIndex] = { ...notes[nearbyIndex] };
        } else {
          // Add to myNotes if not already there
          this.myNotes.unshift({ ...notes[nearbyIndex] });
        }

        // Sync across all nearby caches
        for (const otherNotes of this.nearbyNotesCache.values()) {
          const otherIndex = otherNotes.findIndex((n) => n.id === id);
          if (otherIndex !== -1) {
            otherNotes[otherIndex] = { ...notes[nearbyIndex] };
          }
        }

        return { data: notes[nearbyIndex] };
      }
    }

    throw new Error("Note not found or not authorized");
  }

  // DELETE /api/notes/:id
  async deleteNote(id) {
    await delay(300);

    // Remove from myNotes
    const myIndex = this.myNotes.findIndex((n) => n.id === id);
    if (myIndex !== -1) {
      this.myNotes.splice(myIndex, 1);

      // Remove from nearby caches
      for (const notes of this.nearbyNotesCache.values()) {
        const nearbyIndex = notes.findIndex((n) => n.id === id);
        if (nearbyIndex !== -1) {
          notes.splice(nearbyIndex, 1);
        }
      }

      return { success: true };
    }

    throw new Error("Note not found or not authorized");
  }

  // POST /api/notes/:id/like
  async toggleLike(id) {
    await delay(200);

    // Find note in any store
    let note = this.myNotes.find((n) => n.id === id);
    let noteIndex = this.myNotes.findIndex((n) => n.id === id);
    let inMyNotes = noteIndex !== -1;

    if (!note) {
      for (const notes of this.nearbyNotesCache.values()) {
        note = notes.find((n) => n.id === id);
        if (note) break;
      }
    }

    if (!note) throw new Error("Note not found");

    // Use note.isLikedByCurrentUser as the source of truth
    const wasLiked = note.isLikedByCurrentUser;

    // Toggle the like state
    note.isLikedByCurrentUser = !wasLiked;

    if (wasLiked) {
      // Unlike: decrement count
      note.stats.likes = Math.max(0, note.stats.likes - 1);
    } else {
      note.stats.likes += 1;
    }

    // Update the note in the appropriate store
    if (inMyNotes) {
      this.myNotes[noteIndex] = { ...note };
    }

    // Also update in nearby caches if it exists there
    for (const notes of this.nearbyNotesCache.values()) {
      const nearbyIndex = notes.findIndex((n) => n.id === id);
      if (nearbyIndex !== -1) {
        notes[nearbyIndex] = { ...note };
      }
    }

    return {
      data: {
        liked: note.isLikedByCurrentUser,
        count: note.stats.likes,
      },
    };
  }

  // POST /api/notes/:id/comments
  // Real backend: Gets userId from auth token in request headers
  async addComment(id, commentData, userId = CURRENT_USER.id) {
    await delay(300);

    // Get user info (simulates backend user lookup)
    const user = getUserById(userId);

    // Find note in any store
    let note = this.myNotes.find((n) => n.id === id);

    if (!note) {
      for (const notes of this.nearbyNotesCache.values()) {
        note = notes.find((n) => n.id === id);
        if (note) break;
      }
    }

    if (!note) throw new Error("Note not found");

    const newComment = {
      id: `comment-${Date.now()}`,
      user: {
        id: user.id,
        name: user.name,
        avatar: user.avatar,
      },
      text: commentData.text,
      likes: 0,
      isLikedByCurrentUser: false,
      createdAt: Date.now(),
    };

    note.comments.unshift(newComment);
    note.stats.comments += 1;

    return {
      data: newComment,
      stats: {
        likes: note.stats.likes,
        comments: note.stats.comments,
      },
    };
  }

  // DELETE /api/notes/:id/comments/:commentId
  // Real backend: Gets userId from auth token in request headers
  async deleteComment(noteId, commentId, userId = CURRENT_USER.id) {
    await delay(300);

    if (!userId) throw new Error("User not authenticated");

    // Find note in any store
    let note = this.myNotes.find((n) => n.id === noteId);
    let inMyNotes = note !== undefined;

    if (!note) {
      for (const notes of this.nearbyNotesCache.values()) {
        note = notes.find((n) => n.id === noteId);
        if (note) break;
      }
    }

    if (!note) throw new Error("Note not found");

    // Find the comment
    const commentIndex = note.comments.findIndex((c) => c.id === commentId);
    if (commentIndex === -1) throw new Error("Comment not found");

    const comment = note.comments[commentIndex];

    // Check authorization: user must own the comment OR own the note
    const isCommentOwner = comment.user.id === userId;
    const isNoteOwner = note.owner.id === userId;

    if (!isCommentOwner && !isNoteOwner) {
      throw new Error("Not authorized to delete this comment");
    }

    // Remove the comment
    note.comments.splice(commentIndex, 1);
    note.stats.comments = note.comments.length;

    // Sync changes across all stores
    if (inMyNotes) {
      // Update in myNotes
      const myNoteIndex = this.myNotes.findIndex((n) => n.id === noteId);
      if (myNoteIndex !== -1) {
        this.myNotes[myNoteIndex] = note;
      }
    }

    // Update in nearby caches
    for (const notes of this.nearbyNotesCache.values()) {
      const nearbyIndex = notes.findIndex((n) => n.id === noteId);
      if (nearbyIndex !== -1) {
        notes[nearbyIndex] = note;
      }
    }

    return {
      success: true,
      stats: {
        likes: note.stats.likes,
        comments: note.stats.comments,
      },
    };
  }

  // PATCH /api/notes/:noteId/comments/:commentId
  // Update a comment's text
  // Real backend: Gets userId from auth token in request headers
  async updateComment(noteId, commentId, updates) {
    await delay(300);

    // Find note in any store
    let note = this.myNotes.find((n) => n.id === noteId);
    let inMyNotes = note !== undefined;

    if (!note) {
      for (const notes of this.nearbyNotesCache.values()) {
        note = notes.find((n) => n.id === noteId);
        if (note) break;
      }
    }

    if (!note) throw new Error("Note not found");

    // Find the comment
    const commentIndex = note.comments.findIndex((c) => c.id === commentId);
    if (commentIndex === -1) throw new Error("Comment not found");

    // Update the comment with editedAt timestamp
    note.comments[commentIndex] = {
      ...note.comments[commentIndex],
      ...updates,
      editedAt: Date.now(),
    };

    const updatedComment = note.comments[commentIndex];

    // Sync changes across all stores
    if (inMyNotes) {
      const myNoteIndex = this.myNotes.findIndex((n) => n.id === noteId);
      if (myNoteIndex !== -1) {
        this.myNotes[myNoteIndex] = note;
      }
    }

    // Update in nearby caches
    for (const notes of this.nearbyNotesCache.values()) {
      const nearbyIndex = notes.findIndex((n) => n.id === noteId);
      if (nearbyIndex !== -1) {
        notes[nearbyIndex] = note;
      }
    }

    return {
      data: updatedComment,
    };
  }

  // GET /api/notes/:id/comments
  async getComments(id, { offset = 0, limit = 10 } = {}) {
    await delay(500);

    // Find note in any store
    let note = this.myNotes.find((n) => n.id === id);

    if (!note) {
      for (const notes of this.nearbyNotesCache.values()) {
        note = notes.find((n) => n.id === id);
        if (note) break;
      }
    }

    if (!note) throw new Error("Note not found");

    // Sort comments by createdAt (newest first)
    const sortedComments = [...note.comments].sort(
      (a, b) => b.createdAt - a.createdAt,
    );

    // Comments already have isLikedByCurrentUser from generation or addComment
    const paginatedComments = sortedComments.slice(offset, offset + limit);
    const total = sortedComments.length;
    const hasMore = offset + limit < total;

    return {
      data: paginatedComments,
      total,
      hasMore,
      offset,
      limit,
    };
  }

  // GET /api/notes/:id/stats
  async getNoteStats(id) {
    await delay(100);

    // Find note in any store
    let note = this.myNotes.find((n) => n.id === id);

    if (!note) {
      for (const notes of this.nearbyNotesCache.values()) {
        note = notes.find((n) => n.id === id);
        if (note) break;
      }
    }

    if (!note) throw new Error("Note not found");

    return {
      data: {
        likes: note.stats.likes,
        comments: note.comments.length,
        isLiked: note.isLikedByCurrentUser,
      },
    };
  }

  // POST /api/comments/:commentId/like - Toggle like on a comment
  async toggleCommentLike(noteId, commentId) {
    await delay(200);

    // Find note in any store
    let note = this.myNotes.find((n) => n.id === noteId);

    if (!note) {
      for (const notes of this.nearbyNotesCache.values()) {
        note = notes.find((n) => n.id === noteId);
        if (note) break;
      }
    }

    if (!note) throw new Error("Note not found");

    const comment = note.comments?.find((c) => c.id === commentId);
    if (!comment) throw new Error("Comment not found");

    const wasLiked = comment.isLikedByCurrentUser;

    // Toggle the like state
    comment.isLikedByCurrentUser = !wasLiked;

    if (wasLiked) {
      comment.likes = Math.max(0, comment.likes - 1);
    } else {
      comment.likes += 1;
    }

    // Update in all stores to keep them in sync
    const myIndex = this.myNotes.findIndex((n) => n.id === noteId);
    if (myIndex !== -1) {
      this.myNotes[myIndex] = { ...note };
    }

    for (const notes of this.nearbyNotesCache.values()) {
      const nearbyIndex = notes.findIndex((n) => n.id === noteId);
      if (nearbyIndex !== -1) {
        notes[nearbyIndex] = { ...note };
      }
    }

    return {
      data: {
        liked: comment.isLikedByCurrentUser,
        count: comment.likes,
      },
    };
  }

  // ========== REPORT API ==========

  /**
   * POST /api/notes/:id/report
   * Report a note for inappropriate content
   * Real backend: Gets userId from auth token, stores report in database
   */
  async reportNote(noteId, reason, details = "", userId = CURRENT_USER.id) {
    await delay(300);

    if (!userId) {
      throw new Error("User ID required");
    }

    // Find note to get context for logging
    let note = this.myNotes.find((n) => n.id === noteId);

    if (!note) {
      for (const notes of this.nearbyNotesCache.values()) {
        note = notes.find((n) => n.id === noteId);
        if (note) break;
      }
    }

    if (!note) throw new Error("Note not found");

    // Create report record
    const report = {
      id: `report-${Date.now()}`,
      type: "note",
      targetId: noteId,
      targetOwnerId: note.owner.id,
      reportedBy: userId,
      reason,
      details,
      createdAt: Date.now(),
      status: "pending", // pending, reviewed, actioned
    };

    // Store the report
    this.reportsStore.push(report);

    // Log report for visibility (in real backend, this would go to moderation queue)
    devLog("📢 Note Reported:", {
      noteId: note.id,
      noteTitle: note.title,
      noteOwner: note.owner.name,
      reportedBy: userId,
      reason,
      details,
      timestamp: new Date(report.createdAt).toISOString(),
    });

    return {
      data: {
        success: true,
        reportId: report.id,
        message: "Thank you for your report. Our team will review it shortly.",
      },
    };
  }

  /**
   * POST /api/notes/:noteId/comments/:commentId/report
   * Report a comment for inappropriate content
   * Real backend: Gets userId from auth token, stores report in database
   */
  async reportComment(
    noteId,
    commentId,
    reason,
    details = "",
    userId = CURRENT_USER.id,
  ) {
    await delay(300);

    if (!userId) {
      throw new Error("User ID required");
    }

    // Find note and comment to get context for logging
    let note = this.myNotes.find((n) => n.id === noteId);

    if (!note) {
      for (const notes of this.nearbyNotesCache.values()) {
        note = notes.find((n) => n.id === noteId);
        if (note) break;
      }
    }

    if (!note) throw new Error("Note not found");

    const comment = note.comments?.find((c) => c.id === commentId);
    if (!comment) throw new Error("Comment not found");

    // Create report record
    const report = {
      id: `report-${Date.now()}`,
      type: "comment",
      targetId: commentId,
      targetOwnerId: comment.user.id,
      noteId: noteId,
      reportedBy: userId,
      reason,
      details,
      createdAt: Date.now(),
      status: "pending", // pending, reviewed, actioned
    };

    // Store the report
    this.reportsStore.push(report);

    // Log report for visibility (in real backend, this would go to moderation queue)
    devLog("📢 Comment Reported:", {
      commentId: comment.id,
      commentText:
        comment.text.substring(0, 50) + (comment.text.length > 50 ? "..." : ""),
      commentAuthor: comment.user.name,
      noteId: note.id,
      noteTitle: note.title,
      reportedBy: userId,
      reason,
      details,
      timestamp: new Date(report.createdAt).toISOString(),
    });

    return {
      data: {
        success: true,
        reportId: report.id,
        message: "Thank you for your report. Our team will review it shortly.",
      },
    };
  }

  // ========== NOTIFICATIONS API ==========

  /**
   * Get or initialize notifications for a user
   * Real backend: Would query notifications table by userId
   */
  _getUserNotifications(userId) {
    if (!this.notificationsStore.has(userId)) {
      // Generate initial notifications for this user
      // Pass actual notes so notifications reference real note IDs
      const notifications = generateNotifications(userId, 30, this.myNotes);
      this.notificationsStore.set(userId, notifications);
    }
    return this.notificationsStore.get(userId);
  }

  /**
   * GET /api/notifications
   * Get user's notifications with pagination and filtering
   * Real backend: Gets userId from auth token in headers
   */
  async getNotifications({
    userId = CURRENT_USER.id,
    filter = "all",
    limit = 20,
    offset = 0,
  } = {}) {
    await delay(500);

    if (!userId) {
      throw new Error("User ID required");
    }

    let notifications = this._getUserNotifications(userId);

    // Filter by type if specified
    if (filter !== "all") {
      const filterMap = {
        likes: [NOTIFICATION_TYPES.LIKE, NOTIFICATION_TYPES.COMMENT_LIKE],
        comments: [NOTIFICATION_TYPES.COMMENT],
        system: [NOTIFICATION_TYPES.NEARBY],
      };

      const allowedTypes = filterMap[filter];
      if (allowedTypes) {
        notifications = notifications.filter((n) =>
          allowedTypes.includes(n.type),
        );
      }
    }

    // Sort by createdAt (newest first)
    notifications = [...notifications].sort(
      (a, b) => b.createdAt - a.createdAt,
    );

    // Paginate
    const paginatedNotifications = notifications.slice(offset, offset + limit);
    const hasMore = offset + limit < notifications.length;

    return {
      data: paginatedNotifications,
      total: notifications.length,
      hasMore,
      filter,
    };
  }

  /**
   * GET /api/notifications/stats
   * Get unread counts per category
   * Real backend: Gets userId from auth token in headers
   */
  async getNotificationStats(userId = CURRENT_USER.id) {
    await delay(200);

    if (!userId) {
      throw new Error("User ID required");
    }

    const notifications = this._getUserNotifications(userId);

    const stats = {
      all: 0,
      likes: 0,
      comments: 0,
      system: 0,
    };

    for (const n of notifications) {
      if (!n.read) {
        stats.all += 1;

        if (
          n.type === NOTIFICATION_TYPES.LIKE ||
          n.type === NOTIFICATION_TYPES.COMMENT_LIKE
        ) {
          stats.likes += 1;
        } else if (n.type === NOTIFICATION_TYPES.COMMENT) {
          stats.comments += 1;
        } else if (n.type === NOTIFICATION_TYPES.NEARBY) {
          stats.system += 1;
        }
      }
    }

    return { data: stats };
  }

  /**
   * PATCH /api/notifications/:id/read
   * Mark a notification as read
   * Real backend: Gets userId from auth token, verifies ownership
   */
  async markNotificationRead(notificationId, userId = CURRENT_USER.id) {
    await delay(100);

    if (!userId) {
      throw new Error("User ID required");
    }

    const notifications = this._getUserNotifications(userId);
    const notification = notifications.find((n) => n.id === notificationId);

    if (!notification) {
      throw new Error("Notification not found");
    }

    if (notification.userId !== userId) {
      throw new Error("Unauthorized");
    }

    notification.read = true;

    return { data: notification };
  }

  /**
   * PATCH /api/notifications/read-all
   * Mark all notifications as read (optionally filtered by type)
   * Real backend: Gets userId from auth token
   */
  async markAllNotificationsRead(userId = CURRENT_USER.id, filter = "all") {
    await delay(300);

    if (!userId) {
      throw new Error("User ID required");
    }

    const notifications = this._getUserNotifications(userId);

    // Filter which notifications to mark as read
    const filterMap = {
      likes: [NOTIFICATION_TYPES.LIKE, NOTIFICATION_TYPES.COMMENT_LIKE],
      comments: [NOTIFICATION_TYPES.COMMENT],
      system: [NOTIFICATION_TYPES.NEARBY],
    };

    const allowedTypes = filter === "all" ? null : filterMap[filter];

    let markedCount = 0;
    for (const n of notifications) {
      if (!n.read && (!allowedTypes || allowedTypes.includes(n.type))) {
        n.read = true;
        markedCount += 1;
      }
    }

    return { data: { marked: markedCount } };
  }

  /**
   * POST /api/notifications/last-seen
   * Update user's last seen timestamp for notifications
   * Real backend: Gets userId from auth token, updates database
   */
  async updateLastSeen(userId = CURRENT_USER.id, timestamp = Date.now()) {
    await delay(100);

    if (!userId) {
      throw new Error("User ID required");
    }

    // In a real backend, this would update the user's lastSeenNotificationsAt field
    // For mock, we can store it in a Map
    if (!this.lastSeenStore) {
      this.lastSeenStore = new Map();
    }

    this.lastSeenStore.set(userId, timestamp);

    return { data: { lastSeenAt: timestamp } };
  }

  /**
   * GET /api/notifications/last-seen
   * Get user's last seen timestamp
   * Real backend: Gets userId from auth token, returns from database
   */
  async getLastSeen(userId = CURRENT_USER.id) {
    await delay(100);

    if (!userId) {
      throw new Error("User ID required");
    }

    if (!this.lastSeenStore) {
      this.lastSeenStore = new Map();
    }

    const lastSeenAt =
      this.lastSeenStore.get(userId) || Date.now() - 2 * 60 * 60 * 1000; // Default: 2 hours ago

    return { data: { lastSeenAt } };
  }

  // ========== USER SETTINGS API ==========

  /**
   * GET /api/user/settings
   * Get user's settings
   * Real backend: Gets userId from auth token in headers
   */
  async getUserSettings(userId = CURRENT_USER.id) {
    await delay(300);

    if (!userId) {
      throw new Error("User ID required");
    }

    // Get settings from store or return defaults
    const settings = this.userSettingsStore.get(userId) || {
      defaultVisibility: "public",
      defaultLocationPrecision: "exact",
      defaultShowAddress: true,
      units: "miles",
      theme: "auto",
      showMyLocationOnMap: true,
      showRadiusCircle: true,
      notifications: {
        likes: true,
        comments: true,
        system: true,
      },
    };

    return { data: settings };
  }

  /**
   * PATCH /api/user/settings
   * Update user's settings (partial update)
   * Real backend: Gets userId from auth token, merges updates
   */
  async updateUserSettings(updates, userId = CURRENT_USER.id) {
    await delay(500);

    if (!userId) {
      throw new Error("User ID required");
    }

    // Get current settings
    const current = this.userSettingsStore.get(userId) || {
      defaultVisibility: "public",
      defaultLocationPrecision: "exact",
      defaultShowAddress: true,
      units: "miles",
      theme: "auto",
      showMyLocationOnMap: true,
      showRadiusCircle: true,
      notifications: {
        likes: true,
        comments: true,
        system: true,
      },
    };

    // Deep merge for nested notifications object
    const updated = {
      ...current,
      ...updates,
      notifications: {
        ...current.notifications,
        ...(updates.notifications || {}),
      },
    };

    this.userSettingsStore.set(userId, updated);

    return { data: updated };
  }

  /**
   * POST /api/user/change-password
   * Change user password
   * Real backend: Gets userId from auth token, validates currentPassword hash
   */
  async changePassword(
    userId = CURRENT_USER.id,
    { currentPassword, newPassword },
  ) {
    await delay(700);

    if (!userId) {
      throw new Error("User ID required");
    }

    if (!currentPassword || !newPassword) {
      throw new Error("Both current and new password required");
    }

    // Mock: Just simulate success
    // Real backend would:
    // 1. Hash and compare currentPassword with stored hash
    // 2. Hash newPassword and update database
    // 3. Invalidate existing sessions/tokens

    return { data: { success: true } };
  }

  /**
   * DELETE /api/user/account
   * Delete user account (dangerous action)
   * Real backend: Gets userId from auth token, validates password
   */
  async deleteAccount(userId = CURRENT_USER.id, { password, reason }) {
    await delay(800);

    if (!userId) {
      throw new Error("User ID required");
    }

    if (!password) {
      throw new Error("Password required for account deletion");
    }

    // Mock: Simulate account deletion
    // Real backend would:
    // 1. Validate password
    // 2. Soft-delete or hard-delete user record
    // 3. Delete or anonymize user's notes, comments, etc.
    // 4. Log deletion reason for analytics
    // 5. Invalidate all sessions/tokens

    return { data: { success: true, deletedAt: Date.now() } };
  }

  /**
   * POST /api/feedback/improvement
   * Submit an improvement suggestion
   * Real backend: Gets userId from auth token, stores in database
   */
  async submitImprovement(userId = CURRENT_USER.id, { category, description }) {
    await delay(500);

    if (!userId) {
      throw new Error("User ID required");
    }

    if (!description?.trim()) {
      throw new Error("Description is required");
    }

    const feedback = {
      id: this.nextId++,
      userId,
      type: "improvement",
      category,
      description,
      createdAt: Date.now(),
      status: "new",
    };

    this.feedbackStore.push(feedback);
    devLog("MockAPI: Improvement submitted:", feedback);

    return { data: feedback };
  }

  /**
   * POST /api/feedback/bug
   * Submit a bug report
   * Real backend: Gets userId from auth token, stores in database
   */
  async submitBugReport(userId = CURRENT_USER.id, { category, description }) {
    await delay(500);

    if (!userId) {
      throw new Error("User ID required");
    }

    if (!description?.trim()) {
      throw new Error("Description is required");
    }

    const feedback = {
      id: this.nextId++,
      userId,
      type: "bug",
      category,
      description,
      createdAt: Date.now(),
      status: "new",
    };

    this.feedbackStore.push(feedback);
    devLog("MockAPI: Bug report submitted:", feedback);

    return { data: feedback };
  }

  /**
   * POST /api/feedback/support
   * Submit a support request
   * Real backend: Gets userId from auth token, stores in database
   */
  async submitSupportRequest(
    userId = CURRENT_USER.id,
    { category, description },
  ) {
    await delay(500);

    if (!userId) {
      throw new Error("User ID required");
    }

    if (!description?.trim()) {
      throw new Error("Description is required");
    }

    const feedback = {
      id: this.nextId++,
      userId,
      type: "support",
      category,
      description,
      createdAt: Date.now(),
      status: "new",
    };

    this.feedbackStore.push(feedback);
    devLog("MockAPI: Support request submitted:", feedback);

    return { data: feedback };
  }

  // Clear caches (useful for testing)
  clearCaches() {
    this.nearbyNotesCache.clear();
    this.notificationsStore.clear();
    if (this.lastSeenStore) {
      this.lastSeenStore.clear();
    }
  }
}

// Singleton instance
export const mockAPI = new MockAPI();
